import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface School {
  id: string;
  name: string;
  invite_code: string | null;
  teacher_count: number;
  default_framework_id: string | null;
  /** Who sees scores: 'full' | 'team' | 'trends' (backend services/scoreMode.ts). */
  score_mode?: 'full' | 'team' | 'trends';
}

export interface Member {
  id: string;
  name: string;
  email: string;
  role: 'teacher' | 'school_leader' | 'hybrid' | 'system_admin';
  created_at: string;
  session_count: number;
  last_active_at: string | null;
}

export function useSchool() {
  return useQuery({ queryKey: ['school'], queryFn: () => api<School>('/schools/mine') });
}

export function useMembers() {
  return useQuery({
    queryKey: ['members'],
    // Only the fields the web shows: the endpoint also returns per-person
    // averages, which leaders must not see (development, not judgement).
    queryFn: async () =>
      (await api<{ data: Member[] }>('/schools/mine/members')).data.map((m) => ({
        id: m.id,
        name: m.name,
        email: m.email,
        role: m.role,
        created_at: m.created_at,
        session_count: Number(m.session_count) || 0,
        last_active_at: m.last_active_at,
      })),
  });
}

export function relativeDate(iso: string | null): string {
  if (!iso) return 'No lessons yet';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${days < 14 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
